/**
 * Script after boot modules
 */
import { type SeedkitCore, useSeedkitApi } from '@seedkit/core';
import type { RrMainMenuItemImpl } from '@seedkit/vue';
import { useAppStore } from '@seedkit/vue/stores';
import { useSession, useUserProfileStore } from '@seedkit/vue-administration-module/public';
import { skSettings } from '../shared/icons';
import { bootstrapNotificationsHub } from '../modules/notifications-hub/notifications-hub-bootstrap';
import { bootstrapSidebarUnreadBadges } from '../shared/sidebar-unread-badges/sidebar-unread-badges-bootstrap';
import { usePermissionsStore } from '../shared/permissions/permissions.store';
import { bootstrapSessionExpiryWatchdog } from '../shared/auth/session-expiry-watchdog';
import { setSessionApiBaseUrl } from '../shared/auth/session-lifecycle';

export default async function (seedkit: SeedkitCore) {
  bootstrapNotificationsHub(seedkit);
  bootstrapSidebarUnreadBadges(seedkit);

  try {
    const authApi = useSeedkitApi('Auth') as { http?: { baseUrl?: string } };
    const baseFromClient = String(authApi?.http?.baseUrl || '');
    if (baseFromClient) {
      setSessionApiBaseUrl(baseFromClient);
    }
  } catch {
    // Auth API may be unavailable in some boot paths
  }

  bootstrapSessionExpiryWatchdog(seedkit);
  const appState = useAppStore();
  const profile = useUserProfileStore();
  const permissionsStore = usePermissionsStore();
  const router = seedkit.router;

  seedkit.eventBus.on('login', async () => {
    appState.mainMenu = appState.mainMenu.sort((a: RrMainMenuItemImpl, b: RrMainMenuItemImpl) => (a.order || 0) - (b.order || 1));
    const adminMenuItem: RrMainMenuItemImpl = appState.mainMenu.find((item: RrMainMenuItemImpl) => item.id === 'AdminMenuItem');
    if (adminMenuItem) {
      adminMenuItem.icon = skSettings;
    }

    const permissionsPromise = permissionsStore.syncPermissionsAfterAuth();

    try {
      if (!profile.userProfile?.id) {
        await profile.loadUserProfile().catch(() => undefined);
      }
      await permissionsPromise;
    } catch {
      permissionsStore.allowedOperationNames = new Set();
      permissionsStore.loaded = true;
      permissionsStore.registerAccessibleModuleRoutes();
    } finally {
      permissionsStore.registerAccessibleModuleRoutes();

      if (router.currentRoute.value.name === 'Login') {
        await router.replace({ path: '/' }).catch(() => undefined);
      }

      const session = useSession();
      session.loading = false;
    }
  });

  seedkit.eventBus.on('logout', () => {
    permissionsStore.clear();
  });

  await router.isReady();

  // На странице логина все модули считаются готовыми, но appReady может
  // ещё не успеть выставиться — это ломает первый вход после очистки кеша.
  if (router.currentRoute.value.name === 'Login') {
    appState.appReady = true;
  }

  await permissionsStore.ensureFetchQueued();

  const session = useSession();
  if (!session.isAccessTokenEmpty && !session.isTokenExpired && permissionsStore.allowedOperationNames.size === 0) {
    if (!appState.userInfo?.id) {
      await profile.loadUserProfile().catch(() => undefined);
    }
    await permissionsStore.syncPermissionsAfterAuth();
  }
}
